import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  Area,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ghostBtn, panelClass, selectField } from '../governed-mcp-gateway/components/GatewayUi';
import { logOmniChat } from './omniChatLogger';
import { gatewayProvider, MONITOR_PROVIDERS } from './omniChatSynthetic';
import type { OmniChatHealthLabel, OmniChatMonitorInsight, OmniChatMonitorStatus } from './omniChatTypes';

type RangeDays = 7 | 30;

const LIVE_REFRESH_MS = 15_000;

const PALETTE = {
  conversations: { light: '#4f46e5', dark: '#818cf8' },
  latency: { light: '#d97706', dark: '#fbbf24' },
  allowed: { light: '#059669', dark: '#34d399' },
  flagged: { light: '#d97706', dark: '#fbbf24' },
  blocked: { light: '#dc2626', dark: '#f87171' },
  grid: { light: '#e5e7eb', dark: '#262626' },
  axis: { light: '#737373', dark: '#a3a3a3' },
};

function statusTone(status: OmniChatMonitorStatus | OmniChatHealthLabel, isLight: boolean): string {
  if (status === 'ok' || status === 'Healthy') return isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/15 text-emerald-300';
  if (status === 'warn' || status === 'Watch') return isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300';
  return isLight ? 'bg-red-50 text-red-700' : 'bg-red-500/15 text-red-300';
}

function deltaTone(value: number, invert: boolean, isLight: boolean): string {
  const better = invert ? value <= 0 : value >= 0;
  if (Math.abs(value) < 0.05) return isLight ? 'text-neutral-500' : 'text-neutral-400';
  return better
    ? isLight
      ? 'text-emerald-600'
      : 'text-emerald-300'
    : isLight
      ? 'text-red-600'
      : 'text-red-300';
}

function formatDelta(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
  if (!values.length) return <div className="h-7" />;
  const width = 76;
  const height = 28;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = Math.max(max - min, 1);
  const points = values.map((value, index) => {
    const x = (index / Math.max(values.length - 1, 1)) * width;
    const y = height - 2 - ((value - min) / span) * (height - 4);
    return `${x},${y}`;
  });

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <polyline fill="none" stroke={color} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" points={points.join(' ')} />
    </svg>
  );
}

function ScoreRing({ score, isLight }: { score: number; isLight: boolean }) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, score));
  const color = score >= 85 ? '#10b981' : score >= 70 ? '#f59e0b' : '#ef4444';

  return (
    <svg width="76" height="76" viewBox="0 0 76 76" aria-hidden>
      <circle cx="38" cy="38" r={radius} fill="none" stroke={isLight ? '#e5e7eb' : '#262626'} strokeWidth="7" />
      <circle
        cx="38"
        cy="38"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={`${(clamped / 100) * circumference} ${circumference}`}
        transform="rotate(-90 38 38)"
      />
      <text
        x="38"
        y="42"
        textAnchor="middle"
        fontSize="16"
        fontWeight="600"
        fill={isLight ? '#171717' : '#fafafa'}
      >
        {Math.round(clamped)}
      </text>
    </svg>
  );
}

function formatStamp(value: Date): string {
  return value.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function expandDays(days: OmniChatMonitorInsight['days'], range: RangeDays): OmniChatMonitorInsight['days'] {
  if (range === 7 || days.length === 0) return days;
  const out: OmniChatMonitorInsight['days'] = [];
  for (let ago = range - 1; ago >= 0; ago -= 1) {
    const source = days[(range - 1 - ago) % days.length];
    const date = new Date();
    date.setDate(date.getDate() - ago);
    const week = Math.floor(ago / 7);
    out.push({
      label: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      conversations: Math.max(0, Math.round(source.conversations * (0.78 + week * 0.07))),
      latencyMs: Math.round(source.latencyMs * (1.04 - week * 0.015)),
    });
  }
  return out;
}

function applyFilters(
  insight: OmniChatMonitorInsight,
  gatewayId: string,
  provider: string,
  connectorId: string,
  range: RangeDays,
  tick: number
): OmniChatMonitorInsight {
  const providerScale = provider === 'all' || provider === gatewayProvider(gatewayId) ? 1 : 0.08;
  const connector = insight.connectors.find((row) => row.id === connectorId);
  const connectorScale = connector
    ? (connector.ok + connector.slow + connector.error) /
      Math.max(insight.connectors.reduce((sum, row) => sum + row.ok + row.slow + row.error, 0), 1)
    : 1;
  const scale = providerScale * connectorScale;
  const days = expandDays(insight.days, range).map((day, index, all) => {
    const live = tick && index === all.length - 1 ? tick % 4 : 0;
    return {
      ...day,
      conversations: Math.max(0, Math.round(day.conversations * scale) + live),
      latencyMs: Math.round(day.latencyMs * (providerScale === 1 ? 1 : 1.08) + (tick % 3) * 2),
    };
  });
  const liveRow =
    tick > 0
      ? [{ id: `live-${tick}`, when: 'Just now', event: 'Live conversation completed', status: 'ok' as const }]
      : [];

  return {
    ...insight,
    conversations: Math.round(insight.conversations * scale * (range === 30 ? 3.8 : 1)),
    tokens: Math.round(insight.tokens * scale * (range === 30 ? 3.8 : 1)),
    latencyMs: Math.round(insight.latencyMs + (tick % 3) * 4),
    errorRate: Number((insight.errorRate * (providerScale === 1 ? 1 : 1.4)).toFixed(1)),
    days,
    policies: {
      allowed: Math.round(insight.policies.allowed * scale * (range === 30 ? 3.8 : 1)),
      flagged: Math.round(insight.policies.flagged * scale * (range === 30 ? 3.8 : 1)),
      blocked: Math.round(insight.policies.blocked * scale * (range === 30 ? 3.8 : 1)),
    },
    connectors: connector ? [connector] : insight.connectors,
    blocksToday: insight.blocksToday + (tick % 2),
    activity: [...liveRow, ...insight.activity].slice(0, 8),
  };
}

function ChartTip({
  active,
  payload,
  label,
  isLight,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
  isLight: boolean;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs shadow-sm ${isLight ? 'border-neutral-200 bg-white' : 'border-neutral-700 bg-neutral-900'}`}>
      <p className={`mb-1 font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{label}</p>
      {payload.map((item) => (
        <p key={item.name} style={{ color: item.color }}>
          {item.name}: {item.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
}

export default function OmniChatMonitoringTab({
  agentId,
  gatewayId,
  insight,
  isLight,
}: {
  agentId: string;
  gatewayId: string;
  insight: OmniChatMonitorInsight;
  isLight: boolean;
}) {
  const [range, setRange] = useState<RangeDays>(7);
  const [provider, setProvider] = useState('all');
  const [connectorId, setConnectorId] = useState('all');
  const [live, setLive] = useState(false);
  const [tick, setTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(() => new Date());

  const refresh = (reason: 'refresh' | 'live' | 'filter') => {
    setTick((prev) => prev + 1);
    setUpdatedAt(new Date());
    logOmniChat('app', `monitor_${reason}`, { id: agentId, range, provider, connectorId });
  };

  useEffect(() => {
    setUpdatedAt(new Date());
    logOmniChat('app', 'monitor_filter', { id: agentId, range, provider, connectorId });
  }, [agentId, connectorId, provider, range]);

  useEffect(() => {
    if (!live) return undefined;
    const pulse = () => {
      if (document.hidden) return;
      setTick((prev) => prev + 1);
      setUpdatedAt(new Date());
      logOmniChat('app', 'monitor_live_tick', { id: agentId, range, provider, connectorId });
    };
    pulse();
    const timer = window.setInterval(pulse, LIVE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [live, agentId, range, provider, connectorId]);

  const view = useMemo(
    () => applyFilters(insight, gatewayId, provider, connectorId, range, tick),
    [connectorId, gatewayId, insight, provider, range, tick]
  );

  const ink = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const liveTrackOff = isLight ? 'bg-neutral-300' : 'bg-neutral-600';
  const conversations = view.days.map((day) => day.conversations);
  const latency = view.days.map((day) => day.latencyMs);
  const lastEvent = view.activity[0]?.when ?? '—';
  const axis = isLight ? PALETTE.axis.light : PALETTE.axis.dark;
  const grid = isLight ? PALETTE.grid.light : PALETTE.grid.dark;
  const conversationColor = isLight ? PALETTE.conversations.light : PALETTE.conversations.dark;
  const latencyColor = isLight ? PALETTE.latency.light : PALETTE.latency.dark;
  const policyRows = [
    { name: 'Allowed', value: view.policies.allowed, color: isLight ? PALETTE.allowed.light : PALETTE.allowed.dark },
    { name: 'Flagged', value: view.policies.flagged, color: isLight ? PALETTE.flagged.light : PALETTE.flagged.dark },
    { name: 'Blocked', value: view.policies.blocked, color: isLight ? PALETTE.blocked.light : PALETTE.blocked.dark },
  ];
  const policyTotal = policyRows.reduce((sum, row) => sum + row.value, 0);
  const kpis = [
    { label: 'Conversations', value: view.conversations.toLocaleString(), delta: view.delta.conversations, invert: false, series: conversations },
    { label: 'Tokens', value: view.tokens.toLocaleString(), delta: view.delta.tokens, invert: false, series: conversations.map((value) => Math.round(value * 42)) },
    { label: 'Latency', value: `${view.latencyMs} ms`, delta: view.delta.latencyMs, invert: true, series: latency },
    { label: 'Error rate', value: `${view.errorRate.toFixed(1)}%`, delta: view.delta.errorRate, invert: true, series: view.days.map((_, index) => Number((view.errorRate + (index - 3) * 0.08).toFixed(2))) },
  ];

  return (
    <div className="space-y-4">
      <section className={`${panelClass(isLight)} px-5 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className={`text-sm font-semibold ${ink}`}>Monitoring</p>
            <p className={`mt-0.5 text-xs ${muted}`}>Volume, policy outcomes, and connector health for this agent.</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`text-[11px] tabular-nums ${muted}`}>Updated {formatStamp(updatedAt)}</span>
            <select
              className={selectField(isLight)}
              value={range}
              aria-label="Range"
              onChange={(event) => setRange(Number(event.target.value) === 30 ? 30 : 7)}
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <select
              className={selectField(isLight)}
              value={provider}
              aria-label="Provider"
              onChange={(event) => setProvider(event.target.value)}
            >
              {MONITOR_PROVIDERS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <select
              className={selectField(isLight)}
              value={connectorId}
              aria-label="Connector"
              onChange={(event) => setConnectorId(event.target.value)}
            >
              <option value="all">All connectors</option>
              {insight.connectors.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={`${ghostBtn(isLight)} !h-8 !text-xs`}
              disabled={refreshing}
              title="Reload now"
              onClick={() => {
                setRefreshing(true);
                refresh('refresh');
                window.setTimeout(() => setRefreshing(false), 400);
              }}
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              type="button"
              role="switch"
              aria-checked={live}
              aria-label="Live updates"
              title={`Poll every ${LIVE_REFRESH_MS / 1000} seconds while enabled`}
              className={`${ghostBtn(isLight)} !h-8 !text-xs`}
              onClick={() => {
                const next = !live;
                setLive(next);
                logOmniChat('app', 'monitor_live', { id: agentId, live: next });
              }}
            >
              <span className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${live ? 'bg-indigo-600' : liveTrackOff}`}>
                <span className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${live ? 'translate-x-3' : 'translate-x-0'}`} />
              </span>
              Live
            </button>
          </div>
        </div>
      </section>

      <section className={`${panelClass(isLight)} px-5 py-4 flex flex-wrap items-center gap-5`}>
        <ScoreRing score={view.health.score} isLight={isLight} />
        <div className="min-w-[10rem]">
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Health</p>
          <div className="mt-1.5 flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusTone(view.health.label, isLight)}`}>
              {view.health.label}
            </span>
            <span className={`text-sm ${ink}`}>Last {range} days</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-6">
          <div>
            <p className={`text-[11px] ${muted}`}>Grounded answers</p>
            <p className={`mt-0.5 text-lg font-semibold tabular-nums ${ink}`}>{view.groundedPct}%</p>
          </div>
          <div>
            <p className={`text-[11px] ${muted}`}>Policy blocks today</p>
            <p className={`mt-0.5 text-lg font-semibold tabular-nums ${ink}`}>{view.blocksToday}</p>
          </div>
          <div>
            <p className={`text-[11px] ${muted}`}>Last event</p>
            <p className={`mt-0.5 text-sm font-medium ${ink}`}>{lastEvent}</p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {kpis.map((item) => (
          <div key={item.label} className={`${panelClass(isLight)} p-4`}>
            <div className="flex items-start justify-between gap-2">
              <p className={`text-[11px] font-medium ${muted}`}>{item.label}</p>
              <span className={`text-[11px] tabular-nums ${deltaTone(item.delta, item.invert, isLight)}`}>{formatDelta(item.delta)}</span>
            </div>
            <p className={`mt-1 text-xl font-semibold tabular-nums ${ink}`}>{item.value}</p>
            <div className="mt-2">
              <Sparkline values={item.series} color={item.invert ? latencyColor : conversationColor} />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <section className={`${panelClass(isLight)} p-5 xl:col-span-2`}>
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Conversations and latency</p>
          <p className={`mt-1 text-xs ${muted}`}>Volume as area, latency as the line. Last {range} days.</p>
          <div className="mt-3 h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={view.days} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: axis, fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
                <Tooltip content={<ChartTip isLight={isLight} />} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="conversations"
                  name="Conversations"
                  stroke={conversationColor}
                  fill={conversationColor}
                  fillOpacity={isLight ? 0.16 : 0.22}
                  strokeWidth={2}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="latencyMs"
                  name="Latency"
                  stroke={latencyColor}
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className={`${panelClass(isLight)} p-5`}>
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Policy outcomes</p>
          <p className={`mt-1 text-xs ${muted}`}>How Advanced flags resolved this week.</p>
          <div className="mt-2 h-[168px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={policyRows} dataKey="value" nameKey="name" innerRadius={46} outerRadius={68} paddingAngle={2} stroke="none">
                  {policyRows.map((row) => (
                    <Cell key={row.name} fill={row.color} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTip isLight={isLight} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5">
            {policyRows.map((row) => (
              <div key={row.name} className="flex items-center justify-between text-[12px]">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full" style={{ background: row.color }} />
                  <span className={muted}>{row.name}</span>
                </span>
                <span className={`tabular-nums ${ink}`}>
                  {row.value.toLocaleString()}
                  {policyTotal ? ` · ${Math.round((row.value / policyTotal) * 100)}%` : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <section className={`${panelClass(isLight)} p-5`}>
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Connector health</p>
          <p className={`mt-1 text-xs ${muted}`}>Calls this week from attached connectors.</p>
          <div className="mt-4 space-y-4">
            {view.connectors.length ? (
              view.connectors.map((row) => {
                const total = Math.max(row.ok + row.slow + row.error, 1);
                return (
                  <div key={row.id}>
                    <div className="flex items-center justify-between text-[12px]">
                      <span className={ink}>{row.label}</span>
                      <span className={`tabular-nums ${muted}`}>{total.toLocaleString()}</span>
                    </div>
                    <div className={`mt-1.5 h-2 flex overflow-hidden rounded-full ${isLight ? 'bg-neutral-100' : 'bg-neutral-800'}`}>
                      <div className="bg-emerald-500" style={{ width: `${(row.ok / total) * 100}%` }} />
                      <div className="bg-amber-400" style={{ width: `${(row.slow / total) * 100}%` }} />
                      <div className="bg-red-500" style={{ width: `${(row.error / total) * 100}%` }} />
                    </div>
                    <p className={`mt-1 text-[11px] ${muted}`}>
                      {row.ok} ok · {row.slow} slow · {row.error} error
                    </p>
                  </div>
                );
              })
            ) : (
              <p className={`text-sm ${muted}`}>No connectors on this agent.</p>
            )}
          </div>
        </section>

        <section className={`${panelClass(isLight)} xl:col-span-2 overflow-hidden`}>
          <p className={`px-5 pt-5 text-[11px] font-semibold uppercase tracking-wide ${muted}`}>Activity</p>
          <div className="mt-3">
            {view.activity.map((row, index) => (
              <div
                key={row.id}
                className={`flex items-start gap-3 px-5 py-3 ${
                  index ? (isLight ? 'border-t border-neutral-100' : 'border-t border-neutral-800') : ''
                }`}
              >
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${
                  row.status === 'ok' ? 'bg-emerald-500' : row.status === 'warn' ? 'bg-amber-400' : 'bg-red-500'
                }`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${ink}`}>{row.event}</p>
                  <p className={`mt-0.5 text-[11px] ${muted}`}>{row.when}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${statusTone(row.status, isLight)}`}>
                  {row.status}
                </span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
